import { firstValueFrom } from 'rxjs';
import { ConversationMessage } from '../../../../../../core/src/lib/interface/conversation.interface';
import { ConversationApiService, KeyValuePair } from '@vendasta/conversation';
import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DIRECT_TOOL_CALL_REQUEST_KEY } from '../../../../constants';

@Injectable()
export class ToolCallHandlerService {
  private readonly conversationApiService = inject(ConversationApiService);

  async handleToolCallFromMessage(
    message: ConversationMessage,
    toolCallName: string,
    onStart?: () => void,
    onComplete?: () => void,
  ): Promise<void> {
    const metadata = message?.metadata ?? [];
    const toolCall = this.parseToolCall(metadata as KeyValuePair[]);

    if (toolCall?.toolCallName !== toolCallName) return;

    const domContent = this.extractPageContent();
    const payload = JSON.stringify({
      arguments: {
        toolCallName: toolCallName,
        screenHTMLContent: domContent,
      },
    });

    onStart?.();

    try {
      await firstValueFrom(
        this.conversationApiService.makeToolCall({
          messageOption: {
            messageId: message.messageId,
          },
          payload: payload,
        }),
      );
    } catch (error) {
      if (!(error instanceof HttpErrorResponse && error.status === 409)) {
        onComplete?.();
      }
      console.error('[ToolCallHandlerService] Failed to handle toolcall:', error);
    }
  }

  private parseToolCall(metadata: KeyValuePair[]): { toolCallName: string; arguments: string } | null {
    if (metadata === null) return null;
    const entry = metadata.find((m) => m.key === DIRECT_TOOL_CALL_REQUEST_KEY);

    if (!entry?.value) return null;

    try {
      const parsed = JSON.parse(entry.value);
      if (Array.isArray(parsed.arguments)) {
        const nameEntry = parsed.arguments.find((arg: { key: string }) => arg.key === 'toolCallName');
        const toolCallName = nameEntry?.value;

        return {
          toolCallName,
          arguments: JSON.stringify(parsed.arguments),
        };
      }
    } catch (err) {
      console.warn('Invalid toolcall JSON:', entry.value, err);
    }
    return null;
  }

  extractPageContent(): string {
    const pages = Array.from(document.querySelectorAll('glxy-page'));

    if (pages.length === 0) {
      return document.documentElement.outerHTML;
    }

    const targetPage = pages[0];
    return targetPage?.outerHTML ?? document.documentElement.outerHTML;
  }
}
